import { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { KYB_LINK_PARTNERS, KYB_LINK_REGIONS } from 'modules/merchantinfo/constants/constants';
import { showError, showToast } from 'utils/notifications';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SHAREABLE_FORM_BASE_URL = process.env.REACT_APP_SHAREABLE_FORM_BASE_URL || 'https://dashboard.roma.global';

const buildKybLink = (region, partner) => {
  const token =
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return `${SHAREABLE_FORM_BASE_URL}/form/public/${token}?region=${region}&partner=${partner}`;
};

export default function CreateKybLinkModal({ isOpen, onClose, entityName, requireEmail = true }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [region, setRegion] = useState(KYB_LINK_REGIONS[0]);
  const [partner, setPartner] = useState(KYB_LINK_PARTNERS[0]);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const toast = useToast();

  const handleClose = () => {
    setEmail('');
    setName('');
    setRegion(KYB_LINK_REGIONS[0]);
    setPartner(KYB_LINK_PARTNERS[0]);
    setLink('');
    setCopied(false);
    onClose();
  };

  const handleCreate = () => {
    if (requireEmail && !EMAIL_REGEX.test(email)) {
      showError(toast, 'Invalid email', '', 'Enter a valid recipient email address.');

      return;
    }

    setLink(buildKybLink(region, partner));
    showToast(toast, 'Link created', 'Shareable KYB link generated. Copy it and send it to the client.', 'success');
  };

  useEffect(() => {
    if (isOpen && !requireEmail) setLink(buildKybLink(region, partner));
    else setLink((prev) => (prev ? buildKybLink(region, partner) : prev));
  }, [isOpen, requireEmail, region, partner]);

  const handleCopy = () => {
    if (!link) return;

    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={true} size='xl' isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Shareable KYB Link{entityName ? ` — ${entityName}` : ''}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={4} mb={4}>
            <FormControl>
              <FormLabel color='gray.500' fontWeight='bold'>
                Region
              </FormLabel>
              <Select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                backgroundColor='white'
                color='gray.600'
              >
                {KYB_LINK_REGIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel color='gray.500' fontWeight='bold'>
                Partner
              </FormLabel>
              <Select
                value={partner}
                onChange={(e) => setPartner(e.target.value)}
                backgroundColor='white'
                color='gray.600'
              >
                {KYB_LINK_PARTNERS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Flex>
          {requireEmail && (
            <>
              <FormControl mb={4}>
                <FormLabel color='gray.500' fontWeight='bold'>
                  Recipient email
                </FormLabel>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='client@company.com'
                  isDisabled={!!link}
                  backgroundColor='white'
                  color='gray.600'
                />
              </FormControl>
              <FormControl>
                <FormLabel color='gray.500' fontWeight='bold'>
                  Recipient name (optional)
                </FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='Company or contact name'
                  isDisabled={!!link}
                  backgroundColor='white'
                  color='gray.600'
                />
              </FormControl>
            </>
          )}

          {!!link && (
            <FormControl mt={5}>
              <FormLabel color='gray.500' fontWeight='bold'>
                Shareable link
              </FormLabel>
              <InputGroup>
                <Input value={link} isReadOnly backgroundColor='gray.50' color='gray.600' pr='4.5rem' />
                <InputRightElement width='4.5rem'>
                  <Button size='sm' onClick={handleCopy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <Text fontSize='sm' color='gray.500' mt={1}>
                Copy this link and send it to the client. They can fill the KYB without an account.
              </Text>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex gap={2}>
            <Button size='sm' variant='outline' onClick={handleClose}>
              {link ? 'Done' : 'Cancel'}
            </Button>
            {!link && requireEmail && (
              <Button colorScheme='blue' size='sm' onClick={handleCreate} isDisabled={!email}>
                Create Link
              </Button>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
